import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import api from '@/lib/api';
import { getDeviceInfo } from '@/lib/utils';
import { armWakeBeep, checkWakeBeep } from '@/lib/wakeBeep';

// The Guide runs on the Emergent preview URL. If the backend has gone idle
// (Emergent preview sleeps after inactivity), in-app fetch/XHR calls cannot
// wake it: only a FULL page navigation triggers the platform's native
// "Wake Up Agent" flow. So when we detect a sleeping backend, we reload the
// page and let the native Emergent wake screen take over.

// True for errors that look like "the backend is asleep or unreachable"
// (network layer or gateway timeouts / bad gateways from the platform proxy).
function looksLikeBackendAsleep(err) {
    if (!err) return false;
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
    const s = err.response?.status;
    return s === 502 || s === 503 || s === 504;
}

export default function GuideLoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Change password state
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [token, setToken] = useState(null);

    // Backend-sleep detection: full reload → native Emergent wake screen
    const [serverAsleep, setServerAsleep] = useState(false);

    useEffect(() => {
        checkWakeBeep(() => toast.success('✅ Serveur prêt — vous pouvez vous connecter !', { duration: 8000 }));
    }, []);

    const triggerNativeWake = () => {
        setServerAsleep(true);
        armWakeBeep();
        toast.info('Serveur en veille — rechargement pour le réveiller… Un bip sonnera quand il sera prêt.');
        setTimeout(() => window.location.reload(), 1200);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post('/auth/login?app=guide', { username, password, ...getDeviceInfo() });
            const { token: t, user } = res.data;

            if (user.must_change_password) {
                setToken(t);
                setMustChangePassword(true);
                setLoading(false);
                return;
            }

            localStorage.setItem('guide_token', t);
            localStorage.setItem('guide_user', JSON.stringify(user));
            localStorage.removeItem('guide_selected_site');
            const guideSites = user.guide_sites || [];
            if (guideSites.length === 0) {
                navigate('/guide');
            } else {
                navigate('/guide/select-site');
            }
        } catch (err) {
            if (looksLikeBackendAsleep(err)) {
                triggerNativeWake();
            } else {
                toast.error('Identifiants incorrects');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        setChangingPassword(true);
        try {
            await api.post('/auth/change-password', { new_password: newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Mot de passe modifie avec succes');
            // Re-login with new password
            const res = await api.post('/auth/login?app=guide', { username, password: newPassword, ...getDeviceInfo() });
            const { token: t, user } = res.data;
            localStorage.setItem('guide_token', t);
            localStorage.setItem('guide_user', JSON.stringify(user));
            localStorage.removeItem('guide_selected_site');
            const guideSites = user.guide_sites || [];
            if (guideSites.length === 0) {
                navigate('/guide');
            } else {
                navigate('/guide/select-site');
            }
        } catch {
            toast.error('Erreur lors du changement de mot de passe');
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-700 via-red-600 to-orange-500 flex flex-col items-center justify-center px-6">
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white tracking-tight">Fuji Express</h1>
                <p className="text-white/80 text-sm mt-1">Guide du Sushiman</p>
            </div>

            {serverAsleep && !mustChangePassword && (
                <div
                    className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm mb-4 border border-amber-200 text-center"
                    data-testid="guide-wake-server-card"
                >
                    <p className="text-sm font-semibold text-slate-900">Serveur en veille</p>
                    <p className="text-xs text-slate-500 mt-1">Rechargement en cours — la page de réveil va s&rsquo;afficher…</p>
                </div>
            )}

            {!mustChangePassword ? (
                <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm space-y-4"
                    data-testid="guide-login-form"
                >
                    <h2 className="text-lg font-semibold text-slate-900 text-center">Connexion</h2>
                    <div>
                        <Input
                            type="text"
                            placeholder="Identifiant"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            required
                            className="h-12 text-base"
                            data-testid="guide-email-input"
                        />
                    </div>
                    <div>
                        <Input
                            type="password"
                            placeholder="Mot de passe"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            className="h-12 text-base"
                            data-testid="guide-password-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 text-base bg-red-600 hover:bg-red-700 text-white rounded-xl"
                        data-testid="guide-login-btn"
                    >
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                </form>
            ) : (
                <form
                    onSubmit={handleChangePassword}
                    className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm space-y-4"
                    data-testid="guide-change-password-form"
                >
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                            <Lock className="w-6 h-6 text-amber-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-slate-900 text-center">Nouveau mot de passe</h2>
                        <p className="text-sm text-slate-500 text-center">Veuillez choisir votre propre mot de passe</p>
                    </div>
                    <div>
                        <Input
                            type="password"
                            placeholder="Nouveau mot de passe"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            className="h-12 text-base"
                            data-testid="new-password-input"
                        />
                    </div>
                    <div>
                        <Input
                            type="password"
                            placeholder="Confirmer le mot de passe"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            className="h-12 text-base"
                            data-testid="confirm-password-input"
                        />
                    </div>
                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                    )}
                    <Button
                        type="submit"
                        disabled={changingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                        className="w-full h-12 text-base bg-red-600 hover:bg-red-700 text-white rounded-xl"
                        data-testid="change-password-btn"
                    >
                        {changingPassword ? 'Enregistrement...' : 'Valider'}
                    </Button>
                </form>
            )}
        </div>
    );
}
